import React, { lazy, Suspense } from "react";
import { type AuthProvider, Authenticated, Refine } from "@refinedev/core";
import { AuthPage, useNotificationProvider } from "@refinedev/chakra-ui";
import { ChakraProvider, Center, extendTheme, Image, Spinner, ThemeConfig } from "@chakra-ui/react";
import routerProvider, {
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { InfoIcon, StarIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";

import dataProvider from "@refinedev/simple-rest";
import { useTranslation } from "react-i18next";
import axios from "axios";
// @deprecated V2는 V1으로 통합됨. 필요 시 주석 해제.
// import { AppraisalV2List, AppraisalV2Show } from "./pages/appraisalv2";
import { CustomLayout } from "./components/layout/CustomLayout";
import { API_BASE_URL } from "./contants";
import {
  ADMIN_ENVIRONMENTS,
  buildEnvironmentApiUrl,
  getAdminEnvironment,
  getAdminEnvironmentConfig,
  setAdminEnvironment,
  setAdminEnvironmentAvailability,
  type AdminEnvironment,
} from "./services/adminEnvironment";
import { disableAdminWebPush } from "./services/adminWebPush";
import {
  clearAdminTokens,
  getValidAdminAccessToken,
  installAdminAuthInterceptor,
  saveAdminTokens,
} from "./services/adminAuthSession";
import chatbotDataProvider from "./utility/chatbotDataProvider";
import { springAdminApi } from "./utility/springAdminApi";

installAdminAuthInterceptor(axios);

const lazyNamed = <Props = any>(loader: () => Promise<any>, exportName: string) =>
  lazy(async () => ({ default: (await loader())[exportName] as React.ComponentType<Props> }));

const AppraisalEdit = lazyNamed(() => import("./pages/appraisal"), "AppraisalEdit");
const AppraisalList = lazyNamed(() => import("./pages/appraisal"), "AppraisalList");
const AppraisalShow = lazyNamed(() => import("./pages/appraisal"), "AppraisalShow");
const ContactList = lazyNamed(() => import("./pages/contact"), "ContactList");
const ContactShow = lazyNamed(() => import("./pages/contact"), "ContactShow");
const UsersList = lazyNamed(() => import("./pages/users"), "UsersList");
const UsersShow = lazyNamed(() => import("./pages/users"), "UsersShow");
const CategoryList = lazyNamed(() => import("./pages/category"), "CategoryList");
const CategoryShow = lazyNamed(() => import("./pages/category"), "CategoryShow");
const FaqList = lazyNamed(() => import("./pages/faq"), "FaqList");
const FaqShow = lazyNamed(() => import("./pages/faq"), "FaqShow");
const FaqCreate = lazy(() => import("./pages/faq/create"));
const NoticeList = lazyNamed(() => import("./pages/notices"), "NoticeList");
const NoticeShow = lazyNamed(() => import("./pages/notices"), "NoticeShow");
const NoticeCreate = lazyNamed(() => import("./pages/notices"), "NoticeCreate");
const OfflineList = lazyNamed(() => import("./pages/offline"), "OfflineList");
const OpinionDashboard = lazyNamed(() => import("./pages/opinionDashboard"), "OpinionDashboard");
const SerialManagement = lazyNamed(() => import("./pages/real/SerialManagement"), "SerialManagement");
const GroupManagement = lazyNamed(() => import("./pages/real/GroupManagement"), "GroupManagement");
const GroupDetail = lazyNamed(() => import("./pages/real/GroupDetail"), "GroupDetail");
const OrdersList = lazyNamed(() => import("./pages/orders"), "OrdersList");
const OndaList = lazyNamed(() => import("./pages/onda"), "OndaList");
const OndaShow = lazyNamed(() => import("./pages/onda"), "OndaShow");
const PostingList = lazyNamed(() => import("./pages/posting"), "PostingList");
const PaymentTestDataList = lazyNamed(() => import("./pages/paymentTestData"), "PaymentTestDataList");
const PaymentMonitoringList = lazyNamed(() => import("./pages/paymentMonitoring"), "PaymentMonitoringList");
const DashboardList = lazyNamed(() => import("./pages/dashboard"), "DashboardList");
const FreePeriodPolicyPage = lazyNamed(() => import("./pages/paymentPolicy"), "FreePeriodPolicyPage");
const PushMessagesList = lazyNamed(() => import("./pages/pushMessages"), "PushMessagesList");

const authCredentials = {
  email: "",
  password: "",
};

const RedirectWithId = ({ to }: { to: string }) => {
  const { id } = useParams();
  return <Navigate to={to.replace(":id", id || "")} replace />;
};

const RouteLoading = () => (
  <Center minH="320px" color="var(--app-text-brand)">
    <Spinner size="lg" />
  </Center>
);

// 다크모드 설정: 시스템 설정을 따르려면 useSystemColorMode를 true로 변경하세요.
const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const menuItems = [
  {
    name: "dashboard",
    label: "운영 개요",
    icon: <InfoIcon style={{ width: '24px', height: '24px' }} />,
    route: "/dashboard",
  },
  {
    name: "appraisal",
    label: "감정 관리",
    icon: <Image src='/assets/expert_icon.png' style={{ width: '24px', height: '24px' }} />,
    route: "/appraisal",
    children: [
      { name: "online_appraisal", label: "모바일 - 온라인 감정(V1)", route: "/appraisal", group: "모바일 감정" },
      // @deprecated V2는 V1으로 통합됨. 필요 시 주석 해제.
      // { name: "appraisal_v2", label: "모바일 - 온라인 감정(V2)", route: "/appraisalv2", group: "모바일 감정" },
      { name: "apply_onda", label: "챗봇 감정", route: "/applyonda", group: "챗봇 감정" },
    ]
  },
  {
    name: "offline",
    label: "대한명품감정원",
    icon: <Image src='/assets/offline_icon.png' style={{ width: '28px', height: '28px' }} />,
    route: "/offline",
    children: [
      { name: "opinion_dashboard", label: "소견서 대시보드", route: "/opinion-dashboard", group: "대시보드" },
      { name: "opinioncard", label: "기업-감정번호관리", route: "/opinioncard", group: "감정번호/소견서 관리" },
      { name: "opinioncase", label: "기업-소견서관리", route: "/opinioncase", group: "감정번호/소견서 관리" },
      { name: "popinioncard", label: "개인-감정번호관리", route: "/popinioncard", group: "감정번호/소견서 관리" },
      { name: "popinioncase", label: "개인-소견서관리", route: "/popinioncase", group: "감정번호/소견서 관리" },
      { name: "bopinioncase", label: "이전)소견서관리", route: "/bopinion", group: "이전)기업 감정" },
    ]
  },
  { name: "contact", label: "문의 내역", icon: <Image src='/assets/contact_icon.png' style={{ width: '24px', height: '24px' }} />, route: "/contact" },
  { name: "users", label: "사용자 관리", icon: <Image src='/assets/user_icon.png' style={{ width: '24px', height: '24px' }} />, route: "/users" },
  { name: "categories", label: "카테고리 관리", icon: <Image src='/assets/category_icon.png' style={{ width: '28px', height: '28px' }} />, route: "/categories" },
  { name: "faq", label: "FAQ 관리", icon: <Image src='/assets/faq_icon.png' style={{ width: '28px', height: '28px' }} />, route: "/faq" },
  {
    name: "notices",
    label: "공지 관리",
    icon: <InfoIcon style={{ width: '24px', height: '24px' }} />,
    route: "/notices",
    children: [
      { name: "notices_main", label: "사이트 공지", route: "/notices", group: "공지 관리" },
      { name: "push_messages", label: "FCM 공지 발송", route: "/push-messages", group: "공지 관리" },
    ],
  },
  {
    name: "orders",
    label: "결제 관리",
    icon: <Image src='/assets/orders_icon.png' style={{ width: '28px', height: '28px' }} />,
    route: "/orders",
    children: [
      { name: "orders_main", label: "결제/환불 관리", route: "/orders", group: "결제 관리" },
      { name: "payment_test_data", label: "테스트 결제 데이터 삭제", route: "/payment-test-data", group: "결제 정리" },
      { name: "payment_monitoring", label: "결제 모니터링", route: "/payment-monitoring", group: "결제 관리" },
      { name: "payment_policy", label: "무료 감정 기간 설정", route: "/payment-policy", group: "결제 관리" },
    ]
  },
  { name: "posting", label: "블로그 포스팅 관리", icon: <StarIcon style={{ width: '24px', height: '24px' }} />, route: "/posting" },
];

const App: React.FC = () => {
  const customTheme = extendTheme({
    config,
    colors: {
      brand: {
        50: "#EAF2FB",
        100: "#C9DFF3",
        500: "#014984",
        600: "#003963",
      },
    },
semanticTokens: {
    colors: {
      "app-bg-card":   { default: "white",    _dark: "gray.800" },
      "app-bg-page":   { default: "#EDF2F7",  _dark: "gray.900" },
      // 글자색 토큰이 가장 중요합니다!
      "text-primary":  { default: "gray.800", _dark: "whiteAlpha.900" },
      "text-secondary":{ default: "gray.600", _dark: "whiteAlpha.600" },
      "text-brand":    { default: "#014984",  _dark: "#70B7EF" },
      "border-subtle": { default: "#DCE5EF", _dark: "#38526A" },
      "bg-table-header":{ default: "#EAF2FB", _dark: "#243B54" },

      // 배지용 토큰
      "status-refunded-bg": { default: "black", _dark: "whiteAlpha.900" },
      "status-refunded-text": { default: "white", _dark: "gray.900" },
    },
  },
    components: {
      Alert: {
        baseStyle: {
          container: {
            bg: "app-bg-card", // 토큰 적용
            border: "1px solid",
            borderColor: "gray.200",
            _dark: { borderColor: "gray.600" }
          },
        },
      },
    },
  });

  const authProvider: AuthProvider = {
    login: async ({ email, password }) => {
      const loginEnvironment = async (environment: AdminEnvironment) => {
        try {
          const apiBaseUrl = ADMIN_ENVIRONMENTS[environment].apiBaseUrl;
          if (!apiBaseUrl) throw new Error("서버 주소가 설정되지 않았습니다.");
          const response = await axios.post(
            buildEnvironmentApiUrl(environment, "/online/auth/signin"),
            { email, password },
          );
          const accessToken = response.data?.access_token;
          const refreshToken = response.data?.refresh_token;
          if (!accessToken || !refreshToken) throw new Error("로그인 응답에 토큰이 없습니다.");
          saveAdminTokens(accessToken, refreshToken, environment);
          setAdminEnvironmentAvailability(environment, true);
          return { environment, success: true as const };
        } catch (error: any) {
          clearAdminTokens(environment);
          setAdminEnvironmentAvailability(environment, false);
          return {
            environment,
            success: false as const,
            message: error?.response?.data?.message || error?.message || "네트워크 오류",
          };
        }
      };

      const results = await Promise.all(
        (Object.keys(ADMIN_ENVIRONMENTS) as AdminEnvironment[]).map(loginEnvironment),
      );
      const successful = results.filter((result) => result.success);
      if (successful.length === 0) {
        return { success: false, error: { message: "운영·개발 서버 로그인에 모두 실패했습니다.", name: "Login failed" } };
      }

      const preferredEnvironment = getAdminEnvironment();
      const selectedEnvironment = successful.some((result) => result.environment === preferredEnvironment)
        ? preferredEnvironment
        : successful[0].environment;
      setAdminEnvironment(selectedEnvironment);

      const failed = results.filter((result) => !result.success);
      return {
        success: true,
        redirectTo: "/dashboard",
        ...(failed.length > 0
          ? { error: { message: `${failed.map((result) => ADMIN_ENVIRONMENTS[result.environment].label).join(", ")} 서버는 연결할 수 없습니다.`, name: "Partial login" } }
          : {}),
      };
    },
    logout: async () => {
      await disableAdminWebPush();
      clearAdminTokens();
      return { success: true, redirectTo: "/login" };
    },
    check: async () => {
      let token: string | null;
      try {
        token = await getValidAdminAccessToken();
      } catch {
        return { authenticated: false, logout: true, redirectTo: "/login" };
      }
      if (!token) return { authenticated: false, logout: true, redirectTo: "/login" };
      try {
        const response = await axios.get(buildEnvironmentApiUrl(getAdminEnvironment(), "/admin/users/auth/verify"), {
          headers: { 'access-token': token }
        });
        return response.status === 200 ? { authenticated: true } : { authenticated: false, logout: true, redirectTo: "/login" };
      } catch (error: any) {
        if (!error?.response) return { authenticated: true };
        return { authenticated: false, logout: true, redirectTo: "/login" };
      }
    },
    getPermissions: async () => ["admin"],
    getIdentity: async () => ({ id: "quazar@quazar.co.kr", name: "Admin" }),
    onError: async (error) => {
      if (error?.status === 401) {
        clearAdminTokens();
        return { logout: true, redirectTo: '/login' };
      }
      return { error };
    },
  };

  const { t, i18n } = useTranslation();
  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  return (
    <BrowserRouter>
      <ChakraProvider theme={customTheme}>
        <Refine
          dataProvider={{
            default: dataProvider(
              getAdminEnvironmentConfig().apiBaseUrl || API_BASE_URL,
              springAdminApi,
            ),
            chatbot: chatbotDataProvider,
          }}
          i18nProvider={i18nProvider}
          authProvider={authProvider}
          routerProvider={routerProvider}
          notificationProvider={useNotificationProvider()}
          resources={[
            { name: "online_appraisal", list: "/appraisal", show: "/appraisal/show/:id", edit: "/appraisal/edit/:id" },
            // @deprecated V2는 V1으로 통합됨. 필요 시 주석 해제.
            // { name: "appraisal_v2", list: "/appraisalv2", show: "/appraisalv2/show/:id" },
            { name: "contact", list: "/contact", show: "/contact/show/:id" },
            { name: "users", list: "/users", show: "/users/show/:email" },
            { name: "faq", list: "/faq", show: "/faq/show/:id" },
            { name: "notices", list: "/notices", show: "/notices/show/:id", create: "/notices/create" },
            { name: "push_messages", list: "/push-messages" },
            { name: "offline", list: "/offline", show: "/offline/show/:id" },
            { name: "opinion_dashboard", list: "/opinion-dashboard" },
            { name: "orders", list: "/orders", show: "/orders/show/:id" },
            { name: "payment_test_data", list: "/payment-test-data" },
            { name: "payment_monitoring", list: "/payment-monitoring" },
            { name: "payment_policy", list: "/payment-policy" },
            { name: "cases", list: "/opinioncase", show: "/opinioncase/show/:id", edit: "/opinioncase/edit/:id" },
            { name: "real_cards", list: "/opinioncard", show: "/opinioncard/show/:id", edit: "/opinioncard/edit/:id" },
            { name: "p_real_cards", list: "/popinioncard", show: "/popinioncard/show/:id", edit: "/popinioncard/edit/:id" },
            { name: "p_cases", list: "/popinioncase", show: "/popinioncase/show/:id", edit: "/popinioncase/edit/:id" },
            { name: "apply_onda", list: "/applyonda", show: "/applyonda/show/:id", meta: { dataProviderName: "chatbot" } },
            { name: "message_templates", meta: { dataProviderName: "chatbot" } },
            { name: "message_logs", meta: { dataProviderName: "chatbot" } },
            { name: "opinion_case", list: "/bopinion", show: "/bopinion/show/:id", edit: "/bopinion/edit/:id" },
            { name: "posting", list: "/posting" }
          ]}
          options={{ syncWithLocation: true, warnWhenUnsavedChanges: true }}
        >
          <Routes>
            <Route path="/" element={<Authenticated key="root-redirect" fallback={<Navigate to="/login" />}><Navigate to="/dashboard" /></Authenticated>} />
            <Route path="/aiappraisal" element={<Navigate to="/appraisal" replace />} />
            <Route path="/aiappraisal/show/:id" element={<RedirectWithId to="/appraisal/show/:id" />} />
            <Route path="/aiappraisal/edit/:id" element={<RedirectWithId to="/appraisal/edit/:id" />} />
            <Route path="/exappraisal" element={<Navigate to="/appraisal" replace />} />
            <Route path="/exappraisal/show/:id" element={<RedirectWithId to="/appraisal/show/:id" />} />
            <Route path="/exappraisal/edit/:id" element={<RedirectWithId to="/appraisal/edit/:id" />} />
            <Route element={<Authenticated key="authenticated-routes" fallback={<Navigate to="/login" />}><CustomLayout Title={() => <Image src='/assets/quazar_icon.png' style={{ width: '120px', margin: '0 auto' }} />} menuItems={menuItems}><Suspense fallback={<RouteLoading />}><Outlet /></Suspense></CustomLayout></Authenticated>}>
              <Route path="/dashboard"><Route index element={<DashboardList />} /></Route>
              <Route path="/appraisal"><Route index element={<AppraisalList />} /><Route path="show/:id" element={<AppraisalShow />} /><Route path="edit/:id" element={<AppraisalEdit />} /></Route>
              {/* @deprecated V2는 V1으로 통합됨. 필요 시 주석 해제. */}
              {/* <Route path="/appraisalv2"><Route index element={<AppraisalV2List />} /><Route path="show/:id" element={<AppraisalV2Show />} /></Route> */}
              <Route path="/contact"><Route index element={<ContactList />} /><Route path="show/:id" element={<ContactShow />} /><Route path="edit/:id" element={<AppraisalEdit />} /></Route>
              <Route path="/users"><Route index element={<UsersList />} /><Route path="show/:email" element={<UsersShow />} /></Route>
              <Route path="/categories"><Route index element={<CategoryList />} /><Route path="show/:id" element={<CategoryShow />} /></Route>
              <Route path="/faq"><Route index element={<FaqList />} /><Route path="show/:id" element={<FaqShow />} /><Route path="create" element={<FaqCreate />} /></Route>
              <Route path="/notices"><Route index element={<NoticeList />} /><Route path="show/:id" element={<NoticeShow />} /><Route path="create" element={<NoticeCreate />} /></Route>
              <Route path="/push-messages"><Route index element={<PushMessagesList />} /></Route>
              <Route path="/offline"><Route index element={<OfflineList />} /></Route>
              <Route path="/opinion-dashboard"><Route index element={<OpinionDashboard />} /></Route>
              <Route path="/applyonda"><Route index element={<OndaList />} /><Route path="show/:id" element={<OndaShow />} /></Route>
              <Route path="/applyondaing"><Route index element={<Navigate to="/applyonda" replace />} /><Route path="show/:id" element={<OndaShow />} /></Route>
              <Route path="/applyondaend"><Route index element={<Navigate to="/applyonda" replace />} /><Route path="show/:id" element={<OndaShow />} /></Route>
              <Route path="/applyondaimpo"><Route index element={<Navigate to="/applyonda" replace />} /><Route path="show/:id" element={<OndaShow />} /></Route>
              <Route path="/bopinion"><Route index element={<GroupManagement archive />} /><Route path="show/:id" element={<GroupDetail archive />} /><Route path="edit/:id" element={<GroupDetail archive />} /></Route>
              <Route path="/orders"><Route index element={<OrdersList />} /></Route>
              <Route path="/payment-test-data"><Route index element={<PaymentTestDataList />} /></Route>
              <Route path="/payment-monitoring"><Route index element={<PaymentMonitoringList />} /></Route>
              <Route path="/payment-policy"><Route index element={<FreePeriodPolicyPage />} /></Route>
              <Route path="/opinioncase"><Route index element={<GroupManagement type="company" />} /><Route path="show/:id" element={<GroupDetail type="company" />} /><Route path="edit/:id" element={<GroupDetail type="company" />} /></Route>
              <Route path="/opinioncard"><Route index element={<SerialManagement type="company" />} /><Route path="show/:id" element={<SerialManagement type="company" />} /><Route path="edit/:id" element={<SerialManagement type="company" />} /></Route>
              <Route path="/popinioncard"><Route index element={<SerialManagement type="person" />} /><Route path="show/:id" element={<SerialManagement type="person" />} /><Route path="edit/:id" element={<SerialManagement type="person" />} /></Route>
              <Route path="/popinioncase"><Route index element={<GroupManagement type="person" />} /><Route path="show/:id" element={<GroupDetail type="person" />} /><Route path="edit/:id" element={<GroupDetail type="person" />} /></Route>
              <Route path="/posting"><Route index element={<PostingList />} /></Route>
            </Route>
            <Route element={<Authenticated key="auth-pages" fallback={<Outlet />}><Outlet /></Authenticated>}>
              <Route path="/login" element={<AuthPage rememberMe={false} registerLink={false} type="login" title="관리자 페이지" formProps={{ defaultValues: { ...authCredentials } }} />} />
            </Route>
          </Routes>
          <UnsavedChangesNotifier />
          <DocumentTitleHandler />
        </Refine>
      </ChakraProvider>
    </BrowserRouter>
  );
};

export default App;
